import React, { useState, useEffect } from 'react';
import { authService } from '../../services/api';
import axios from 'axios';
import AuthCard from './AuthCard';
import SelfSignupCheckEmail from './SelfSignupCheckEmail';
import { Link, useParams } from 'react-router-dom';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { CircularProgress } from '@mui/material';

const SelfSignup = () => {
  const { slug: urlSlug } = useParams(); // workspace slug from /signup/:slug
  const [formData, setFormData] = useState({
    email: ''
  });
  const [status, setStatus] = useState('success'); // 'loading' | 'success' | 'error' | 'check-email' | 'is-internal' | 'is-registered'
  const [errorMsg, setErrorMsg] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [clientSlug, setClientSlug] = useState(urlSlug || null);
  
  /* ── Email submit ── */  
  const handleSignUpSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email.trim()) {
      setErrorMsg('Please enter a valid email address');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const response = await authService.selfSignupEmail(formData.email);
      if (response.data.success) {
        if (response.data.isExternal && response.data.active === false) {
            setStatus('external-inactive');
        }
        else if (response.data.isExternal && response.data.active === 'invited') {            
            setStatus('external-invited');
        }
        else {
          setSubmittedEmail(formData.email);
          setStatus('check-email');
        }
      } else {
        // A Deactivated internal account sees the agreed words too (S-6.1 pt 5). Removed 18 Oct 2026 (S-6.1 pt 5):
        // showing it the "Internal account" card.
        if (response.data.isInternal && response.data.active === false) {
          setStatus('external-inactive');
        }
        // If internal user
        else if (response.data.isInternal) {
          setErrorMsg('Self-signup is not available for internal users. Please contact your administrator.');
          setStatus('is-internal');
        }
        // If already registered
        else if (response.data.isRegistered) {
          //console.log("Email already registered:", formData.email);
          setSubmittedEmail(formData.email);
          //setErrorMsg('This email is already registered. Please log in instead.');
          setStatus('is-registered');
        } else {
          setErrorMsg(response.data.message || 'Failed to send signup link');
        }
      }
    } catch (error) {
      // With self-signup off, or at its cap, the email step refuses a new person (N47 pt 3): the page's 'Signups are
      // currently closed' screen, as its own check on opening shows, not a message under the email field.
      if (error.response?.data?.signupsClosed) {
        setErrorMsg('');
        setStatus('cap-error');
        return;
      }
      // A refusal (a switched-off workspace or an ended free trial: 403) or a failure shows its message on the form, as
      // on the forgot-password page (S-14, K11 pt 2, S-19 (f)). Removed 18 Oct 2026 (S-14): the 'error' status, which this
      // page has no view for, so the page went blank.
      setStatus('success');
      setErrorMsg(error.response?.data?.message || 'Failed to send signup link. Please try again.');
    }
  };

  const handleResendMagicLink = async () => {
    setErrorMsg('');
    setStatus('loading');

    try {
      const response = await authService.selfSignupEmail(submittedEmail);
      if (response.data.success && response.data.isExternal && response.data.active === false) {
        // Deactivated since the first send: the agreed inactive card, as the first send shows (S-6.1 pt 5). Removed 18 Oct
        // 2026 (S-6.1 pt 5): staying on this card, which still said "Magic link sent" though nothing was sent.
        setStatus('external-inactive');
      } else if (response.data.success) {
        setStatus('is-registered');
        setErrorMsg('A new magic link has been sent. Check your inbox.');
      } else {
        setStatus('is-registered');
        setErrorMsg(response.data.message || 'Failed to resend magic link');
      }
    } catch (error) {
      // A refusal or a failed send shows its message on the form, as the first send's does (S-14, S-19 (f), with commit
      // 80). Removed 18 Oct 2026 (S-14): staying on this card, which never shows the message, so it still said "Magic
      // link sent".
      setStatus('success');
      setErrorMsg(error.response?.data?.message || 'Failed to resend magic link');
    }
  };

  /* ── Logo fetch ── */
  useEffect(() => {
    const fetchLogo = async () => {
        try {
            const res = await axios.get('/api/settings/logo', urlSlug ? { params: { slug: urlSlug } } : undefined);
            if (res.data.success && res.data.logoUrl) {
                setLogoUrl(res.data.logoUrl);
            }
            else
            {
                setLogoUrl("/default-logo.png"); // Set to default logo if not provided
            }
            setClientSlug(urlSlug || res.data.slug || null);
        } catch (err) {
            console.error("Failed to fetch logo", err);
        }
    };
    
    fetchLogo();
  }, []);

  /* ── Logo fetch ── */
  useEffect(() => {
    const checkUserCap = async () => {
        try {
          const res = await authService.selfUserCapCheck(urlSlug);
          console.log("Self-signup user cap check response:", res.data);
          if (res.data.success) {
            if (res.data.selfSignupEnabled === false) {
                setStatus('cap-error');
                //setErrorMsg('Self-signup is currently disabled for this workspace.');
                //return;
            }

            if (res.data.selfSignupUserCapReached === true) {
                setStatus('cap-error');
                //setErrorMsg('The maximum number of self-signup users has been reached for this workspace.');
                //return;
            }
          }
          else if (res.data.clientNotFound === true) {
            setStatus('workspace-error');
            //setErrorMsg('Workspace not found.');
          }
          else {
            console.error("Unexpected response from selfUserCapCheck:", res.data);
          }
        } catch (err) {
          console.error("Failed to user cap check", err);
        }
    };
    
    checkUserCap();
  }, []);

  

  /* ────── Loading view ────── */
  if (status === 'loading') {
      return (
      <AuthCard logoUrl={logoUrl}>
          <div className="ac-body" style={{ textAlign: 'center', padding: '40px 0' }}>
              <CircularProgress />
              <div className="ac-title" style={{ marginTop: '20px' }}>Sending magic link...</div>
          </div>
          <div className="ac-footer" />
      </AuthCard>
      );
  }

  /* ────────────────────────────────────────────────── Success ── */
  if (status === 'success') {
    return (
      <AuthCard logoUrl={logoUrl}>        
        <div className="ac-body">
          <div className="ac-top">            
            <div className="ac-title">Start learning</div>
            <div className="ac-sub">Enter your email to access training resources.</div>
          </div>
          <form onSubmit={handleSignUpSubmit} >            
            <div className="ac-field">
              <div className="ac-field-label">Email</div>                
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="ac-field-input"
                placeholder="e.g. john@gmail.com"
                disabled={status === 'checking'}
                required
              />
              {/* The email step's refusal or failure (S-14, K11 pt 2, S-19 (f)). */}
              {errorMsg && <div className="ac-field-err">{errorMsg}</div>}
            </div>
            <button type="submit" className="ac-btn-primary" disabled={status === 'checking'}>
              {status === 'checking' ? 'Sending link...' : 'Send magic link'}
            </button>                        
          </form>
          <div className="ac-hint">Your learning activity will be visible to Your Organization to help improve your experience.</div>          
        </div>
        <div className="ac-footer" />
      </AuthCard>
    );
  }
  else if (status === 'check-email')
  {
    return (
      // A Resend refused because sign-ups closed since the first send gets the same closed screen (N47 pt 3).
      <SelfSignupCheckEmail email={submittedEmail} logoUrl={logoUrl} onClosed={() => { setErrorMsg(''); setStatus('cap-error'); }} />
    );
  }  
  else if (status === 'is-internal')
  {
    /* ─────────────────────────────────────────────────── Error ── */
    return (
      <AuthCard logoUrl={logoUrl}>
        <div className="ac-top">
          <div className="ac-icon ac-icon-warn">
              <span style={{ fontSize: '24px' }}><WarningAmberOutlinedIcon /></span>
          </div>
          <div className="ac-title">Internal account</div>
          <div className="ac-sub">This email belongs to your organization’s internal team.</div>
        </div>
        <div className="ac-body">       
          <div className="ac-box ac-box-error">
            <div className="ac-box-title">Self-signup not available</div>
            <div className="ac-box-sub">Internal accounts are set up by your administrator. Check your email for an invitation, or contact your administrator for access.</div>
          </div>
          <div className="ac-field-link">
              Already have an account? <Link to={`/learn/${clientSlug}`} onClick={() => setStatus('email-input')} className="ac-link">Sign in</Link>.
          </div>          
        </div>
        <div className="ac-footer" />
      </AuthCard>
    );
  }
  else if (status === 'external-inactive')
  {
    /* ─────────────────────────────────────────────────── Error ── */
    return (
      <AuthCard logoUrl={logoUrl}>
        <div className="ac-top">
          <div className="ac-icon ac-icon-error">
              <span style={{ fontSize: '24px' }}><CloseOutlinedIcon /></span>
          </div>
          {/* A Deactivated account sees the agreed words only, as at every other step (S-6.1 pt 5, agreed 17 Oct).
              Removed 17 Oct 2026 (S-6.1 pt 5): "Account inactive. Your account has been deactivated. Please contact
              your administrator for access.", and its "Cannot sign up" box. */}
          <div className="ac-title">Your account is inactive.</div>
          <div className="ac-sub">Please contact your administrator.</div>
        </div>
        <div className="ac-body">       
          <div className="ac-field-link">
              <Link to={`/learn/${clientSlug}`} onClick={() => setStatus('email-input')} className="ac-link">Go to sign in</Link>.
          </div>          
        </div>
        <div className="ac-footer" />
      </AuthCard>
    );
  }
  else if (status === 'external-invited')
  {
    /* ─────────────────────────────────────────────────── Error ── */
    return (
      <AuthCard logoUrl={logoUrl}>
        <div className="ac-top">
          <div className="ac-icon ac-icon-warn">
              <span style={{ fontSize: '24px' }}><WarningAmberOutlinedIcon /></span>
          </div>
          <div className="ac-title">Invitation already sent</div>
          <div className="ac-sub">You already have a pending invitation. We’ve resent your activation link.</div>
        </div>
        <div className="ac-body">       
          <div className="ac-box ac-box-warn">
            <div className="ac-box-title">Activation link resent</div>
            <div className="ac-box-sub">Check your email for a fresh magic link. It expires in 72 hours.</div>
          </div>
          <div className="ac-field-link">
              <Link to={`/learn/${clientSlug}`} onClick={() => setStatus('email-input')} className="ac-link">Go to sign in</Link>.
          </div>          
        </div>
        <div className="ac-footer" />
      </AuthCard>
    );
  }
  else if (status === 'is-registered')
  {
    /* ─────────────────────────────────────────────────── is-registered ── */
    return (
      <AuthCard logoUrl={logoUrl}>
        <div className="ac-top">
          <div className="ac-icon ac-icon-warn">
              <span style={{ fontSize: '24px' }}><WarningAmberOutlinedIcon /></span>
          </div>
          <div className="ac-title">You already have an account</div>
          <div className="ac-sub">We’ve sent a magic link to  <strong>{submittedEmail}</strong>. to sign you in.</div>
        </div>
        <div className="ac-body">       
          <div className="ac-box ac-box-success">
            <div className="ac-box-title">Magic link sent</div>
            <div className="ac-box-sub">Check your email. Link expires in 15 minutes.</div>
          </div>
          <div className="ac-field-link">Didn’t receive it? <Link className="ac-link" onClick={handleResendMagicLink}> Resend</Link>.</div>          
        </div>
        <div className="ac-footer" />
      </AuthCard>
    );
  }
  else if (status === 'cap-error')
  {
    /* ─────────────────────────────────────────────────── Cap-Error ── */
    return (
      <AuthCard logoUrl={logoUrl}>
        <div className="ac-top">
          <div className="ac-icon ac-icon-warn">
              <span style={{ fontSize: '24px' }}><LockOutlinedIcon /></span>
          </div>
          <div className="ac-title">Signups are currently closed</div>
          <div className="ac-sub">New account registration is not available at this time.</div>
        </div>
        <div className="ac-body">       
          <div className="ac-box ac-box-info">
            <div className="ac-box-sub">Contact your administrator for access. If you already have an account, <Link to={`/learn/${clientSlug}`} onClick={() => setStatus('email-input')} className="ac-link">sign in here</Link>.</div>
          </div>                  
        </div>
        <div className="ac-footer" />
      </AuthCard>
    );
  }
  else if (status === 'workspace-error')
  {
    /* ─────────────────────────────────────────────────── Workspace-Error ── */
    return (
      <AuthCard logoUrl={logoUrl}>
        <div className="ac-top">
          <div className="ac-icon ac-icon-error">
              <span style={{ fontSize: '24px' }}><CloseOutlinedIcon /></span>
          </div>
          <div className="ac-title">Workspace not found</div>
          <div className="ac-sub">The URL you visited doesn’t match any workspace.</div>
        </div>
        <div className="ac-body">       
          <div className="ac-box ac-box-error">
            <div className="ac-box-title">Check the URL</div>
            <div className="ac-box-sub">Make sure the workspace name in the URL is correct. It should look like: <br/><strong>app.beetsol.com/signup/your-company</strong>.</div>
          </div>
          <div className="ac-field-link">If you’re unsure, contact your administrator for the correct link.</div>
        </div>
        <div className="ac-footer" />
      </AuthCard>
    );
  }
};

export default SelfSignup;
